/* A follow request is a relationship state, not an established follow. */
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else Object.defineProperty(root, "LouderrFollowState", { value: Object.freeze(api) });
})(typeof window === "undefined" ? globalThis : window, function () {
  "use strict";
  const unconfirmed = "Couldn’t confirm the current follow status. Check status before trying again.";
  const sameOwner = (a, b) => a.length === b.length && a.every((part, i) => part === b[i]);
  const authorValid = author => author && typeof author.id === "string" && author.id.length > 0;
  // A 4xx other than a timeout is the server's definitive answer: the write was refused
  // (rate limit, block, age gate). Its reason must reach the member; a lost answer must not.
  const refusal = error => {
    const status = Number(error?.status);
    const message = typeof error?.message === "string" ? error.message.trim() : "";
    return status >= 400 && status < 500 && status !== 408 && message ? message : "";
  };
  function create({ api, owner, changed = () => {} }) {
    let identity = null, revision = 0, ownerEpoch = 0;
    const entries = new Map();
    const readOwner = () => {
      const value = owner();
      return Array.isArray(value) ? [...value] : [];
    };
    function clear(next) {
      for (const entry of entries.values()) entry.operation?.controller.abort();
      entries.clear(); identity = next; revision += 1; ownerEpoch = revision;
    }
    function syncIdentity() {
      const next = readOwner();
      if (identity && sameOwner(identity, next)) return false;
      clear(next); return true;
    }
    function reset() { clear(readOwner()); }
    function version() { syncIdentity(); return revision; }
    function isCurrent(author, capturedVersion) {
      syncIdentity();
      if (!authorValid(author) || !Number.isSafeInteger(capturedVersion)
          || capturedVersion < ownerEpoch || capturedVersion > revision) return false;
      const entry = entries.get(author.id);
      return !entry?.operation && capturedVersion >= (entry?.lastMutationRevision || ownerEpoch);
    }
    function snapshot(entry) {
      return {
        following: entry.following, requested: entry.requested,
        access: entry.access, visibility: entry.visibility,
        busy: Boolean(entry.operation), uncertain: entry.uncertain,
        error: entry.error, known: entry.known,
      };
    }
    function entryFor(author, fallbackFollowing = false) {
      if (!authorValid(author)) return null;
      let entry = entries.get(author.id);
      if (!entry) {
        entry = {
          author: { ...author }, fallback: Boolean(fallbackFollowing),
          following: Boolean(fallbackFollowing), requested: false,
          access: null, visibility: null, known: false, uncertain: false, error: null,
          profile: null, status: null, recoveryRequired: false, operation: null, lastMutationRevision: 0,
        };
        entries.set(author.id, entry);
      } else {
        entry.author = { ...entry.author, ...author };
        if (!entry.known && !entry.uncertain && !entry.operation) {
          entry.fallback = Boolean(fallbackFollowing);
          entry.following = entry.fallback;
        }
      }
      return entry;
    }
    function get(author, fallbackFollowing = false) {
      syncIdentity();
      const entry = entryFor(author, fallbackFollowing);
      return entry ? snapshot(entry) : {
        following: Boolean(fallbackFollowing), requested: false, access: null, visibility: null,
        busy: false, uncertain: false, error: null, known: false,
      };
    }
    function emit(entry, reason) {
      // A repaint failure must not strand an acknowledged operation in busy state.
      try { changed({ ...entry.author }, snapshot(entry), reason); }
      catch (error) { globalThis.console?.error?.("Could not repaint follow state", error); }
    }
    function profileValue(author, profile) {
      if (profile?.user?.id !== author.id || typeof profile?.relationship?.is_following !== "boolean") {
        throw new Error("Invalid follow relationship response");
      }
      return { following: profile.relationship.is_following };
    }
    function statusValue(author, status) {
      if (status?.user?.id !== author.id || !["private", "public"].includes(status.profile_visibility)
          || !["open", "requested", "closed"].includes(status.viewer_access)
          || (status.profile_visibility === "public" && status.viewer_access !== "open")) {
        throw new Error("Invalid follow access response");
      }
      return { visibility: status.profile_visibility, access: status.viewer_access };
    }
    function consistent(profile, status, author) {
      return !profile || !status || status.visibility !== "private" || author.id === identity[0]
        || profile.following === (status.access === "open");
    }
    function derive(entry) {
      const { profile, status } = entry;
      if (!consistent(profile, status, entry.author)
          || (entry.recoveryRequired && (!profile || !status))) {
        entry.known = false; entry.uncertain = true; entry.error = unconfirmed;
        return;
      }
      // A public profile is open to everyone. Only its relationship says followed.
      entry.following = status?.visibility === "private" && entry.author.id !== identity[0]
        ? status.access === "open" : profile ? profile.following : entry.fallback;
      entry.requested = status?.access === "requested";
      entry.visibility = status?.visibility || null;
      entry.access = status?.access || null;
      entry.known = Boolean(profile || status?.visibility === "private");
      entry.uncertain = false; entry.error = null; entry.recoveryRequired = false;
    }
    function accept(author, values, capturedVersion) {
      syncIdentity();
      if (!isCurrent(author, capturedVersion) || !values
          || (!values.profile && !values.status)) return false;
      const existing = entries.get(author.id);
      if (existing?.operation) return false;
      let profile, status;
      try {
        if (values.profile) profile = profileValue(author, values.profile);
        if (values.status) status = statusValue(author, values.status);
      } catch (_) { return false; }
      const entry = entryFor(author, existing?.following || false);
      if (profile) entry.profile = profile;
      if (status) entry.status = status;
      derive(entry); emit(entry, "accepted"); return true;
    }
    function current(entry, operation) {
      syncIdentity();
      return entries.get(entry.author.id) === entry && entry.operation === operation
        && sameOwner(operation.identity, identity) && !operation.controller.signal.aborted;
    }
    function ignored(entry = null) {
      return { outcome: "ignored", following: Boolean(entry?.following),
        requested: Boolean(entry?.requested), message: "" };
    }
    function currentMessage(entry) {
      if (entry.requested) return "Current status: request pending.";
      return entry.following
        ? `Current status: following ${entry.author.display_name || entry.author.handle || "this account"}.`
        : "Current status: not following.";
    }
    function finish(entry, operation, outcome, message) {
      if (!current(entry, operation)) return ignored();
      entry.operation = null;
      operation.controller.abort();
      revision += 1; entry.lastMutationRevision = revision;
      const result = { outcome, following: entry.following, requested: entry.requested, message,
        ...(entry.error ? { error: entry.error } : {}) };
      emit(entry, outcome);
      return result;
    }
    async function readCurrent(entry, operation) {
      const author = operation.author;
      if (typeof author.handle !== "string" || !author.handle.trim()) throw new Error("Missing profile handle");
      const read = async path => {
        if (!current(entry, operation)) return null;
        return api(path, { signal: operation.controller.signal });
      };
      const handle = encodeURIComponent(author.handle);
      const [profileResponse, statusResponse] = await Promise.all([
        read(`/api/profiles/${handle}`), read(`/api/status/profiles/${handle}`),
      ]);
      if (!current(entry, operation)) return false;
      const profile = profileValue(author, profileResponse);
      const status = statusValue(author, statusResponse);
      if (!consistent(profile, status, author)) throw new Error("Follow status changed during the read");
      entry.profile = profile; entry.status = status; entry.recoveryRequired = false;
      derive(entry); return true;
    }
    async function recover(entry, operation, cancellation = null) {
      try {
        if (!current(entry, operation) || !await readCurrent(entry, operation)) return ignored();
        if (cancellation?.withdrawn === true && !entry.following && !entry.requested) {
          return finish(entry, operation, "current", "Request cancelled");
        }
        return finish(entry, operation, "reconciled", currentMessage(entry));
      } catch (_) {
        if (!current(entry, operation)) return ignored();
        // Keep the last displayed truth, but do not let it choose the next write.
        entry.profile = null; entry.status = null; entry.recoveryRequired = true;
        entry.known = false; entry.uncertain = true; entry.error = unconfirmed;
        return finish(entry, operation, "uncertain", unconfirmed);
      }
    }
    function acknowledged(entry, following, requested) {
      entry.profile = { following };
      if (requested) entry.status = { visibility: "private", access: "requested" };
      else if (entry.visibility) entry.status = {
        visibility: entry.visibility,
        access: entry.visibility === "public" || following ? "open" : "closed",
      };
      else entry.status = null;
      entry.recoveryRequired = false; derive(entry);
    }
    async function act(author, fallbackFollowing = false) {
      syncIdentity();
      if (!identity[0] || !authorValid(author) || author.id === identity[0]) return ignored();
      const entry = entryFor(author, fallbackFollowing);
      if (entry.operation) return ignored(entry);
      const checking = entry.uncertain;
      const action = entry.requested ? "cancel" : entry.following ? "unfollow" : "follow";
      const operation = { identity: [...identity], author: { ...entry.author }, controller: new AbortController() };
      entry.operation = operation; entry.error = null; revision += 1; entry.lastMutationRevision = revision;
      emit(entry, "busy");
      if (!current(entry, operation)) return ignored();
      if (checking) return recover(entry, operation);
      try {
        const prefix = action === "cancel" ? "/api/follow-requests/" : "/api/follows/";
        const result = await api(`${prefix}${encodeURIComponent(author.id)}`, {
          method: action === "follow" ? "POST" : "DELETE", csrf: true, signal: operation.controller.signal,
        });
        if (!current(entry, operation)) return ignored();
        if (action === "cancel") {
          if (typeof result?.withdrawn !== "boolean") throw new Error("Invalid request withdrawal response");
          // Approval may win the race with withdrawal. Read the actual relationship.
          return recover(entry, operation, result);
        }
        if (action === "unfollow") {
          if (typeof result?.removed !== "boolean") throw new Error("Invalid unfollow response");
          acknowledged(entry, false, false);
          return finish(entry, operation, "current", "Unfollowed");
        }
        if (result?.user?.id !== author.id || typeof result.created !== "boolean") {
          throw new Error("Invalid follow response");
        }
        if (result.requested === true) {
          if (result.created !== false || result.follow_id !== null) throw new Error("Invalid pending response");
          acknowledged(entry, false, true);
          return finish(entry, operation, "current", "Request sent");
        }
        if (typeof result.follow_id !== "string" || !result.follow_id) throw new Error("Missing follow confirmation");
        acknowledged(entry, true, false);
        return finish(entry, operation, "current", `Following ${author.display_name || author.handle || "this account"}`);
      } catch (error) {
        if (!current(entry, operation)) return ignored();
        const refused = refusal(error);
        const result = await recover(entry, operation);
        // Reconcile what is shown. If the requested change still did not happen (another
        // tab may have made it meanwhile), say why this attempt was refused.
        const achieved = action === "follow" ? result.following || result.requested
          : action === "unfollow" ? !result.following : !result.requested;
        return refused && result.outcome === "reconciled" && !achieved
          ? { ...result, message: refused, error: refused } : result;
      }
    }
    syncIdentity();
    return Object.freeze({ version, isCurrent, get, accept, act, reset, syncIdentity });
  }
  return { create };
});
